import axios from "axios";
import { beginApiRequest, endApiRequest } from "./apiRequestState";

const DEFAULT_RETRY_COUNT = 1;
const DEFAULT_RETRY_DELAY_MS = 750;
const NON_RETRYABLE_CLIENT_ERRORS = new Set([400, 401, 403, 404]);
const DEFAULT_RETRY_METHODS = new Set(["get", "head", "options"]);

const parseEnvNumber = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const getDefaultRetryCount = () =>
  parseEnvNumber(
    process.env.REACT_APP_API_RETRY_COUNT ?? process.env.VITE_API_RETRY_COUNT,
    DEFAULT_RETRY_COUNT
  );

const getDefaultRetryDelay = () =>
  parseEnvNumber(
    process.env.REACT_APP_API_RETRY_DELAY_MS ?? process.env.VITE_API_RETRY_DELAY_MS,
    DEFAULT_RETRY_DELAY_MS
  );

const resolveRetryCount = (config) => {
  if (typeof config.retryCount === "number") {
    return Math.max(0, config.retryCount);
  }

  return getDefaultRetryCount();
};

const resolveRetryDelay = (config) => {
  if (typeof config.retryDelayMs === "number") {
    return Math.max(0, config.retryDelayMs);
  }

  return getDefaultRetryDelay();
};

const isRequestCancelled = (error, config) => {
  if (axios.isCancel(error)) return true;
  if (error?.code === "ERR_CANCELED") return true;
  if (config?.signal?.aborted) return true;
  return false;
};

const isRetryableMethod = (config = {}) => {
  if (config.retryUnsafeMethods === true) {
    return true;
  }

  const method = String(config.method || "get").toLowerCase();
  return DEFAULT_RETRY_METHODS.has(method);
};

const isRetryableError = (error, config) => {
  if (isRequestCancelled(error, config)) return false;

  const status = error?.response?.status;
  if (typeof status === "number") {
    if (status >= 500) return true;
    if (NON_RETRYABLE_CLIENT_ERRORS.has(status) && !config.retryOnClientErrors) {
      return false;
    }
    return !!config.retryOnClientErrors && status >= 400 && status < 500;
  }

  const code = error?.code;
  if (code === "ECONNABORTED" || code === "ERR_NETWORK") return true;

  const message = String(error?.message || "").toLowerCase();
  if (message.includes("timeout") || message.includes("network")) return true;

  return !error?.response;
};

const createCancelError = () => new axios.CanceledError("Request cancelled during retry wait");

const waitBeforeRetry = (delayMs, config) =>
  new Promise((resolve, reject) => {
    if (!delayMs) {
      resolve();
      return;
    }

    let completed = false;
    let abortHandler = null;

    const cleanup = () => {
      if (config?.signal && abortHandler) {
        config.signal.removeEventListener("abort", abortHandler);
      }
    };

    const timer = setTimeout(() => {
      if (completed) return;
      completed = true;
      cleanup();
      resolve();
    }, delayMs);

    abortHandler = () => {
      if (completed) return;
      completed = true;
      clearTimeout(timer);
      cleanup();
      reject(createCancelError());
    };

    if (config?.signal) {
      if (config.signal.aborted) {
        abortHandler();
        return;
      }
      config.signal.addEventListener("abort", abortHandler, { once: true });
    }

    if (config?.cancelToken?.promise) {
      config.cancelToken.promise.then(abortHandler).catch(() => {});
    }
  });

export const attachRetryInterceptor = (instance) => {
  if (instance.__retryInterceptorAttached) return;
  instance.__retryInterceptorAttached = true;

  instance.interceptors.request.use(
    (config) => {
      if (config.noRetry) return config;

      if (typeof config.__retryAttempt !== "number") {
        config.__retryAttempt = 0;
      }
      if (typeof config.__retryMaxCount !== "number") {
        config.__retryMaxCount = resolveRetryCount(config);
      }
      if (typeof config.__retryDelayMs !== "number") {
        config.__retryDelayMs = resolveRetryDelay(config);
      }

      beginApiRequest(config);
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      endApiRequest(response?.config);
      return response;
    },
    async (error) => {
      const config = error?.config;

      if (!config || config.noRetry) {
        endApiRequest(config);
        return Promise.reject(error);
      }

      if (!isRetryableMethod(config)) {
        endApiRequest(config);
        return Promise.reject(error);
      }

      if (isRequestCancelled(error, config)) {
        endApiRequest(config);
        return Promise.reject(error);
      }

      const attempts = config.__retryAttempt || 0;
      const maxRetryCount =
        typeof config.__retryMaxCount === "number"
          ? config.__retryMaxCount
          : resolveRetryCount(config);

      if (!isRetryableError(error, config) || attempts >= maxRetryCount) {
        endApiRequest(config);
        return Promise.reject(error);
      }

      config.__retryAttempt = attempts + 1;

      try {
        await waitBeforeRetry(config.__retryDelayMs, config);
      } catch (retryWaitError) {
        endApiRequest(config);
        return Promise.reject(retryWaitError);
      }

      return instance(config);
    }
  );
};
